import { useEffect, useState } from "react"
import { toast } from "sonner"
import { FLOW_NODE_KIND_ICON } from "@/components/flow/flow-node-title"
import type { FlowSidePanelProps } from "@/components/flow/flow-side-panel-props"
import { FlowVideoSourceSlot, flowVideoSourceName } from "@/components/flow/flow-video-source-slot"
import { GenerateWithModelLabel } from "@/components/media/generate-with-model-label"
import { SegmentedControl } from "@/components/ui/segmented-control"
import { AgentColumnHeader } from "@/components/workspace/agent-column-header"
import { useTranslation } from "@/hooks/use-translation"
import { confirmDelayKey } from "@/lib/confirm-delay-registry"
import { depthFlowNode, updateFlowNode } from "@/lib/flow-api"
import { flowNodePending } from "@/types/flow"

type DepthVariant = "gray" | "vis"

function depthVariant(value: string | null | undefined): DepthVariant {
  return value === "vis" ? "vis" : "gray"
}

export function FlowDepthPanel({
  node,
  nodes,
  projectId,
  frameFormat,
  frameRate,
  canEdit,
  onDisconnectEdge,
  onNodeChange,
  onAttach,
}: FlowSidePanelProps) {
  const { t } = useTranslation("flow")
  const [color, setColor] = useState<DepthVariant>(depthVariant(node.model_params?.variant))
  const [generating, setGenerating] = useState(false)
  const sourceInput = (node.inputs ?? []).find(
    (input) => input.target_port === "video" || input.source_port === "video",
  )
  const source = sourceInput ? nodes.find((row) => row.id === sourceInput.source_node_id) : undefined
  const pending = flowNodePending(node) || generating

  useEffect(() => {
    setColor(depthVariant(node.model_params?.variant))
  }, [node.id, node.model_params])

  const selectColor = (id: string) => {
    if (!canEdit || pending) return
    if (id !== "gray" && id !== "vis") return
    const next: DepthVariant = id === "vis" ? "vis" : "gray"
    setColor(next)
    void updateFlowNode(projectId, node.id, {
      model_params: { ...node.model_params, variant: next },
    })
      .then((updated) => {
        if (updated.id === node.id) onNodeChange(updated)
      })
      .catch((error: unknown) => {
        setColor(depthVariant(node.model_params?.variant))
        toast.error(error instanceof Error ? error.message : t("errors.nodeFailed"))
      })
  }

  const handleGenerate = () => {
    if (!canEdit || pending || !sourceInput) return
    setGenerating(true)
    void depthFlowNode(projectId, node.id, { variant: color })
      .then((updated) => onNodeChange(updated))
      .catch((error: unknown) => {
        toast.error(error instanceof Error ? error.message : t("errors.nodeFailed"))
      })
      .finally(() => setGenerating(false))
  }

  const sourceLabels = { video: t("upscale.video"), upscale: t("menu.upscale"), depth: t("menu.depth") }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <AgentColumnHeader icon={FLOW_NODE_KIND_ICON.video_depth} title={t("menu.depth")} />
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-3">
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
          <FlowVideoSourceSlot
            label={t("upscale.source")}
            mediaLabel={t("chain.video")}
            emptyHint={t("upscale.needsSource")}
            uploadLabel={t("chain.upload")}
            libraryLabel={t("chain.library")}
            disconnectLabel={t("upscale.disconnect")}
            source={source}
            sourceName={source ? flowVideoSourceName(source, sourceLabels) : t("upscale.video")}
            connected={Boolean(sourceInput)}
            canEdit={canEdit}
            disabled={pending}
            projectId={projectId}
            frameFormat={frameFormat}
            frameRate={frameRate}
            onDisconnect={() => {
              if (sourceInput) onDisconnectEdge(sourceInput.id)
            }}
            onAttach={onAttach}
          />
          <div className="flex flex-col gap-1.5">
            <p className="text-xs text-ui-text-muted">{t("depth.variant")}</p>
            <SegmentedControl
              value={color}
              disabled={!canEdit || pending}
              onChange={selectColor}
              className="self-start"
              items={[
                { id: "gray", label: t("depth.gray") },
                { id: "vis", label: t("depth.color") },
              ]}
            />
          </div>
        </div>
        {canEdit ? (
          <GenerateWithModelLabel
            label={t("column.generate")}
            busyLabel={t("column.generating")}
            modelLabel={t("depth.model")}
            busy={pending}
            disabled={node.batch_waiting === true || !sourceInput}
            disabledReason={node.batch_waiting ? t("bulk.queued") : undefined}
            onClick={handleGenerate}
            confirmDelayKey={confirmDelayKey("flow", node.id, "video_depth")}
            size="lg"
            className="min-h-[calc(3rem+0.25rem+13px)] w-full shrink-0 items-stretch"
            buttonClassName="w-full"
          />
        ) : null}
      </div>
    </div>
  )
}
